class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onOverlayClick = (event) => {
      if (this.blockOverlayClose) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      this.close();
    };

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.onOverlayClick);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Mobile Firefox can dispatch the tap's click after the drawer is already
    // open. That click lands on the overlay and closes the drawer immediately,
    // so the add looks like it did nothing. Opening from the cart icon passes
    // the trigger element and should still close on the first overlay tap.
    if (!triggeredBy) {
      this.blockOverlayClose = true;
      window.clearTimeout(this.overlayTimer);
      this.overlayTimer = window.setTimeout(() => {
        this.blockOverlayClose = false;
      }, 500);
    }

    // The drawer fades in with a visibility transition. If that transition
    // starts in the same frame as the cart HTML replacement, Firefox keeps the
    // used value at hidden even after the open class is applied. Cancel the
    // transition so the open state paints, then let CSS take over again.
    const openToken = {};
    this.openToken = openToken;
    const inner = this.querySelector('.drawer__inner');
    this.classList.add('animate', 'active');
    this.style.setProperty('transition', 'none', 'important');
    this.style.setProperty('visibility', 'visible', 'important');
    if (inner) {
      inner.style.setProperty('transition', 'none', 'important');
      inner.style.setProperty('transform', 'translateX(0)', 'important');
    }
    requestAnimationFrame(() => {
      if (this.openToken !== openToken) return;
      this.style.removeProperty('transition');
      this.style.removeProperty('visibility');
      if (inner) {
        inner.style.removeProperty('transition');
        inner.style.removeProperty('transform');
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.openToken = null;
    this.classList.remove('active');
    this.style.removeProperty('transition');
    this.style.removeProperty('visibility');
    const inner = this.querySelector('.drawer__inner');
    if (inner) {
      inner.style.removeProperty('transition');
      inner.style.removeProperty('transform');
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.onOverlayClick);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
